import { gsap } from "../../animations/gsapSetup.ts";
import { AnimatedHeadline } from "../../components/common/AnimatedHeadline.tsx";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useIsMobile } from "../../hooks/useMediaQuery.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./SessionHistory.module.css";

interface SessionCard {
  id: string;
  date: string;
  fg: string;
  zones: string[];
}

const SESSIONS: SessionCard[] = [
  {
    id: "session-1",
    date: "2026-03-15",
    fg: "62%",
    zones: ["Paint: 8/10", "Mid-Left: 4/7", "Corner 3: 3/6"],
  },
  {
    id: "session-2",
    date: "2026-03-13",
    fg: "55%",
    zones: ["Restricted: 6/8", "Elbow: 3/6", "Top Key: 2/5"],
  },
  {
    id: "session-3",
    date: "2026-03-11",
    fg: "48%",
    zones: ["Wing 3: 5/12", "Corner 3: 4/8", "Mid-Right: 3/5"],
  },
  {
    id: "session-4",
    date: "2026-03-09",
    fg: "71%",
    zones: ["Paint: 10/12", "Restricted: 5/6", "Elbow: 6/8"],
  },
  {
    id: "session-5",
    date: "2026-03-07",
    fg: "58%",
    zones: ["Top 3: 3/7", "Wing 3: 4/9", "Mid-Left: 5/8"],
  },
];

export function SessionHistory() {
  const reducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const { t } = useI18n();

  const ref = useGsapScrollTrigger(
    (el) => {
      if (reducedMotion) return;

      const track = el.querySelector(`.${styles.track}`);
      const cards = el.querySelectorAll(`.${styles.card}`);

      if (isMobile) {
        gsap.from(cards, {
          y: 40,
          opacity: 0,
          duration: 0.8,
          stagger: 0.15,
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        });
        return;
      }

      if (!track) return;

      const totalScroll = track.scrollWidth - window.innerWidth;

      gsap.to(track, {
        x: -totalScroll,
        ease: "none",
        scrollTrigger: {
          trigger: el,
          pin: true,
          scrub: 1,
          start: "top top",
          end: `+=${totalScroll}`,
        },
      });
    },
    [isMobile],
  );

  return (
    <section ref={ref} className={styles.section}>
      <div className={styles.pinWrap}>
        <div className={styles.heading}>
          <AnimatedHeadline text={t.classic.sessionTitle} as="h2" className={styles.title} />
        </div>
        <div className={styles.track}>
          {SESSIONS.map((session) => (
            <div key={session.id} className={styles.card}>
              <span className={styles.cardDate}>{session.date}</span>
              <span className={styles.cardFg}>{session.fg}</span>
              <span className={styles.cardFgLabel}>{t.classic.sessionFgLabel}</span>
              <div className={styles.zones}>
                {session.zones.map((zone) => (
                  <span key={zone} className={styles.zone}>
                    {zone}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
